import React, { useMemo, useState } from "react";
import { createCompetitionMatch } from "../../../api";
import { CompetitionStage, Match, TournamentParticipant } from "../../../types";
import { toApiDateTimeValue } from "../../../utils/datetime";
import { AnchoredModal } from "../AnchoredModal";
import { Button } from "../Button";
import { SelectField, TextField } from "../form/FormField";
import { useTournamentEvent } from "./TournamentEventContext";

interface AddPlacementMatchModalProps {
  stage: CompetitionStage;
  // Partecipanti candidati (quelli dello stage).
  participants: TournamentParticipant[];
  // Match già presenti nello stage (per calcolare piazzamenti/partecipanti liberi).
  existingMatches: Match[];
  onClose: () => void;
}

// Modale per aggiungere a mano un match di "finale di piazzamento" a uno stage
// knockout di tipo "final": l'utente sceglie i due partecipanti e la coppia di
// posizioni assegnate (1°-2°, 3°-4°, …). Il match nasce "Programmato"; il
// risultato si registra poi dalla modale di modifica.
export const AddPlacementMatchModal: React.FC<AddPlacementMatchModalProps> = ({
  stage,
  participants,
  existingMatches,
  onClose,
}) => {
  const { enableCalendar, onResultSaved } = useTournamentEvent();

  const [player1, setPlayer1] = useState<number | null>(null);
  const [player2, setPlayer2] = useState<number | null>(null);
  const [placementStart, setPlacementStart] = useState<number | "">("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("60");
  const [isSaving, setIsSaving] = useState(false);

  // Posizioni di partenza già occupate da finali esistenti dello stage.
  const takenStarts = useMemo(
    () =>
      new Set(
        existingMatches
          .map((match) => match.ranking_start_position)
          .filter((value): value is number => value != null)
      ),
    [existingMatches]
  );

  // Coppie di piazzamento ancora libere: base = ranking_start_position dello stage,
  // una coppia ogni 2 posizioni per quanti sono i partecipanti.
  const availablePairs = useMemo(() => {
    const base = stage.ranking_start_position || 1;
    const pairCount = Math.floor(participants.length / 2);
    return Array.from({ length: pairCount }, (_, index) => base + index * 2)
      .filter((start) => !takenStarts.has(start))
      .map((start) => ({ start, end: start + 1 }));
  }, [participants.length, stage.ranking_start_position, takenStarts]);

  // Partecipanti già impegnati in un match dello stage: esclusi dalle select.
  const usedParticipantIds = useMemo(() => {
    const used = new Set<number>();
    existingMatches.forEach((match) => {
      if (match.player1 != null) used.add(match.player1);
      if (match.player2 != null) used.add(match.player2);
    });
    return used;
  }, [existingMatches]);

  const candidates = useMemo(
    () => participants.filter((participant) => !usedParticipantIds.has(participant.id)),
    [participants, usedParticipantIds]
  );

  const handleSave = async () => {
    if (player1 == null || player2 == null) {
      alert("Seleziona entrambi i partecipanti.");
      return;
    }
    if (player1 === player2) {
      alert("I partecipanti devono essere diversi.");
      return;
    }
    if (placementStart === "") {
      alert("Seleziona il piazzamento.");
      return;
    }
    const parsedDuration = Number(durationMinutes);
    if (enableCalendar && (!Number.isInteger(parsedDuration) || parsedDuration < 1)) {
      alert("Inserisci una durata valida in minuti.");
      return;
    }

    try {
      setIsSaving(true);
      await createCompetitionMatch({
        stage: stage.id,
        player1,
        player2,
        status: "scheduled",
        ranking_start_position: placementStart,
        ranking_end_position: placementStart + 1,
        ...(enableCalendar
          ? { scheduled_at: toApiDateTimeValue(scheduledAt), duration_minutes: parsedDuration }
          : {}),
      });
      await onResultSaved();
      onClose();
    } catch (error) {
      console.error(error);
      alert("Errore nella creazione del match: " + error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnchoredModal
      title="Aggiungi finale di piazzamento"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving || !availablePairs.length}>
            Aggiungi
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {availablePairs.length === 0 ? (
          <p className="text-sm text-gray-600">
            Tutti i piazzamenti di questo stage hanno già una finale.
          </p>
        ) : (
          <SelectField
            label="Piazzamento"
            value={placementStart}
            onChange={(event) => setPlacementStart(event.target.value === "" ? "" : Number(event.target.value))}
          >
            <option value="">Seleziona piazzamento</option>
            {availablePairs.map((pair) => (
              <option key={pair.start} value={pair.start}>
                {pair.start}° - {pair.end}°
              </option>
            ))}
          </SelectField>
        )}

        <SelectField
          label="Partecipante 1"
          value={player1 ?? ""}
          onChange={(event) => setPlayer1(event.target.value === "" ? null : Number(event.target.value))}
        >
          <option value="">Seleziona partecipante</option>
          {candidates.map((participant) => (
            <option key={participant.id} value={participant.id} disabled={player2 === participant.id}>
              {participant.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          label="Partecipante 2"
          value={player2 ?? ""}
          onChange={(event) => setPlayer2(event.target.value === "" ? null : Number(event.target.value))}
        >
          <option value="">Seleziona partecipante</option>
          {candidates.map((participant) => (
            <option key={participant.id} value={participant.id} disabled={player1 === participant.id}>
              {participant.name}
            </option>
          ))}
        </SelectField>

        {enableCalendar && (
          <div className="grid gap-3 grid-cols-2">
            <label className="block">
              Data e ora
              <input
                type="datetime-local"
                className="w-full border border-gray-300 px-2 py-1 rounded"
                value={scheduledAt}
                onChange={(event) => setScheduledAt(event.target.value)}
              />
            </label>
            <TextField
              label="Durata min"
              type="number"
              min={1}
              value={durationMinutes}
              onChange={(event) => setDurationMinutes(event.target.value)}
            />
          </div>
        )}
      </div>
    </AnchoredModal>
  );
};
